import { PropertyModelEvent, SavedModelScenario, ScenarioLineItem } from '../types';

const normalizeLineItemYear = (value: number | null | undefined) => Math.max(1, Math.round(Number(value || 1)));

export const getScenarioLineItemLabel = (item: ScenarioLineItem): string => {
  if (item.label.trim()) return item.label.trim();
  if (item.type === 'tax_increase') return 'Tax increase';
  if (item.type === 'insurance') return 'Insurance increase';
  if (item.type === 'repair') return 'Repair';
  if (item.type === 'rent_change') return 'Rent change';
  if (item.type === 'vacancy_change') return 'Vacancy change';
  return 'Expense';
};

export const createEmptyScenarioLineItem = (year = 1): ScenarioLineItem => ({
  id: crypto.randomUUID(),
  label: '',
  type: 'expense',
  valueType: 'fixed',
  value: 0,
  year,
  startYear: year,
  endYear: year,
  recurrence: 'one_time',
  notes: ''
});

export const isScenarioLineItemValid = (item: ScenarioLineItem): boolean => (
  Number(item.value || 0) > 0
);

export const getScenarioAnalysisYear = (items: ScenarioLineItem[]): number => {
  const activeYears = items
    .filter(isScenarioLineItemValid)
    .map((item) => {
      if (item.recurrence === 'recurring') return normalizeLineItemYear(item.startYear || item.year);
      return normalizeLineItemYear(item.year);
    });
  return activeYears.length ? Math.min(...activeYears) : 1;
};

export const scenarioLineItemToModelEvent = (
  item: ScenarioLineItem,
  propertyId: string,
  scenarioName: string
): PropertyModelEvent => {
  const category = item.type === 'repair'
    ? 'maintenance'
    : item.type === 'rent_change'
      ? 'income'
      : 'expense';

  return {
    id: `scenario-${item.id}`,
    propertyId,
    name: item.label.trim() || `${scenarioName}: ${getScenarioLineItemLabel(item)}`,
    type: item.recurrence,
    category,
    driver: item.type,
    valueType: item.valueType === 'percent' ? 'percent' : 'amount',
    amount: item.valueType === 'fixed' ? Number(item.value || 0) : 0,
    percent: item.valueType === 'percent' ? Number(item.value || 0) : undefined,
    year: item.recurrence === 'one_time' ? normalizeLineItemYear(item.year) : null,
    startYear: item.recurrence === 'recurring' ? normalizeLineItemYear(item.startYear || item.year) : null,
    endYear: item.recurrence === 'recurring'
      ? Math.max(
          normalizeLineItemYear(item.startYear || item.year),
          normalizeLineItemYear(item.endYear || item.startYear || item.year)
        )
      : null,
    createdAt: Date.now(),
    notes: item.notes || '',
    status: 'active'
  };
};

export const getScenarioModelEvents = (scenario: SavedModelScenario | null | undefined): PropertyModelEvent[] => {
  if (!scenario) return [];
  return (scenario.items || [])
    .filter(isScenarioLineItemValid)
    .map((item) => scenarioLineItemToModelEvent(item, scenario.propertyId, scenario.name));
};

export const migrateLegacyScenarioItems = (events: PropertyModelEvent[] = []): ScenarioLineItem[] => (
  events.map((event) => ({
    id: event.id || crypto.randomUUID(),
    label: event.name || '',
    type: event.driver || (event.category === 'maintenance' ? 'repair' : event.category === 'income' ? 'rent_change' : 'expense'),
    valueType: event.valueType === 'percent' ? 'percent' : 'fixed',
    value: event.valueType === 'percent' ? Number(event.percent || event.amount || 0) : Number(event.amount || 0),
    year: normalizeLineItemYear(event.year || event.startYear || 1),
    startYear: event.type === 'recurring' ? normalizeLineItemYear(event.startYear || event.year || 1) : null,
    endYear: event.type === 'recurring'
      ? Math.max(
          normalizeLineItemYear(event.startYear || event.year || 1),
          normalizeLineItemYear(event.endYear || event.startYear || event.year || 1)
        )
      : null,
    recurrence: event.type === 'recurring' ? 'recurring' : 'one_time',
    notes: event.notes || ''
  }))
);
